import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { AbstractControl, FormGroup, ValidationErrors } from '@angular/forms';
import { Subscription } from 'rxjs';
import {
  IQuestionnaireChoice,
  IQuestionnaireQuestion,
} from 'src/app/interfaces/Questionnaire/IQuestionnaire';

interface TenMeterWalkTestSpeed {
  // The section whose title names the speed.
  section: string;
  // Trial 1 and the optional trial 2, times in seconds.
  trials: string[];
}

// The level of assistance is the number in "(n)" at the start, 1 to 7.
const TEN_METER_WALK_TEST_LEVEL_REGEX = /^\s*\(\s*(\d+)\s*\)/;
const TEN_METER_WALK_TEST_TOTAL_ASSISTANCE = 1;
const TEN_METER_WALK_TEST_LEVEL = 'question837';

// Titles separate the instruction with "||" or "‖".
const TEN_METER_WALK_TEST_TITLE_SEPARATOR = /\|\||‖/;

// The speed is timed over the middle 6 m of the 10 m walkway.
const TEN_METER_WALK_TEST_TIMED_METRES = 6;

// The comfortable speed is the backend score; the fast one is only shown.
const TEN_METER_WALK_TEST_SPEEDS: TenMeterWalkTestSpeed[] = [
  { section: 'question831', trials: ['question832', 'question833'] },
  { section: 'question834', trials: ['question835', 'question836'] },
];
const TEN_METER_WALK_TEST_TRIALS = TEN_METER_WALK_TEST_SPEEDS.flatMap(
  (speed) => speed.trials,
);

// A time above 0 with a dot for decimals, as the backend reads it.
const TEN_METER_WALK_TEST_TIME_PATTERN = /^\d+(\.\d+)?$/;

function isValidTime(value: string): boolean {
  return TEN_METER_WALK_TEST_TIME_PATTERN.test(value) && Number(value) > 0;
}

function timeValidator(control: AbstractControl): ValidationErrors | null {
  const value = String(control.value ?? '').trim();
  return !value || isValidTime(value) ? null : { time: true };
}

@Component({
  selector: 'app-ten-meter-walk-test-questions',
  templateUrl: './ten-meter-walk-test-questions.component.html',
  styleUrl: './ten-meter-walk-test-questions.component.css',
  standalone: false,
})
export class TenMeterWalkTestQuestionsComponent implements OnInit, OnDestroy {
  @Input() elements: IQuestionnaireQuestion[] = [];
  // The page title holds the test's heading and instruction.
  @Input() pageTitle = '';
  @Input() formGroup!: FormGroup;
  @Input() readonlyMode = false;

  private sub?: Subscription;

  constructor(private readonly cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    for (const trial of TEN_METER_WALK_TEST_TRIALS) {
      const control = this.formGroup.get(trial);
      control?.addValidators(timeValidator);
      control?.updateValueAndValidity({ emitEvent: false });
    }

    this.sub = this.formGroup.valueChanges.subscribe(() => {
      this.normaliseTimes();
    });
    this.normaliseTimes();
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  onRadioClick(controlName: string, value: any): void {
    if (this.readonlyMode) return;
    const control = this.formGroup.get(controlName);
    if (!control) return;

    if (control.value === value) {
      control.setValue(null);
    }
  }

  getQuestionTitle(title?: string): string {
    return title?.split(TEN_METER_WALK_TEST_TITLE_SEPARATOR)[0]?.trim() ?? '';
  }

  getQuestionInstruction(title?: string): string {
    return title?.split(TEN_METER_WALK_TEST_TITLE_SEPARATOR)[1]?.trim() ?? '';
  }

  isInvalid(element: IQuestionnaireQuestion): boolean {
    return !!this.formGroup.get(element.name)?.invalid;
  }

  // The speed shown after the last trial of its group.
  getSpeedAfter(
    element: IQuestionnaireQuestion,
  ): TenMeterWalkTestSpeed | undefined {
    return TEN_METER_WALK_TEST_SPEEDS.find(
      (speed) => speed.trials[speed.trials.length - 1] === element.name,
    );
  }

  getSpeedTitle(speed: TenMeterWalkTestSpeed): string {
    return this.getQuestionTitle(
      this.elements.find((el) => el.name === speed.section)?.title,
    );
  }

  getSpeedDisplay(speed: TenMeterWalkTestSpeed): string {
    const value = this.calculateSpeed(speed);
    return value === null ? '–' : value.toFixed(2);
  }

  // Average of 6 m / time over the trials filled in, to two decimals, the
  // same formula as the backend. Level 1 means the patient cannot walk: 0.
  private calculateSpeed(speed: TenMeterWalkTestSpeed): number | null {
    if (this.isTotalAssistance()) {
      return 0;
    }

    const times = speed.trials
      .map((trial) => String(this.formGroup.get(trial)?.value ?? '').trim())
      .filter((time) => time !== '');
    if (!times.length || !times.every((time) => isValidTime(time))) {
      return null;
    }

    const average =
      times.reduce(
        (sum, time) => sum + TEN_METER_WALK_TEST_TIMED_METRES / Number(time),
        0,
      ) / times.length;
    return Math.round(average * 100) / 100;
  }

  // Only a number can be typed or pasted: digits and one dot for decimals.
  // Greek keyboards type "12,5"; the backend reads only "12.5".
  private normaliseTimes(): void {
    for (const trial of TEN_METER_WALK_TEST_TRIALS) {
      const control = this.formGroup.get(trial);
      if (!control || control.disabled) continue;

      const value = String(control.value ?? '');
      const [whole, ...decimals] = value
        .replace(/,/g, '.')
        .replace(/[^\d.]/g, '')
        .split('.');
      const allowed = decimals.length
        ? `${whole}.${decimals.join('')}`
        : whole;

      if (allowed !== value) {
        control.setValue(allowed, { emitEvent: false });
      }
    }

    this.cdr.detectChanges();
  }

  private isTotalAssistance(): boolean {
    const selectedValue = this.formGroup.get(TEN_METER_WALK_TEST_LEVEL)?.value;
    const choice = (
      this.elements?.find((el) => el.name === TEN_METER_WALK_TEST_LEVEL)
        ?.choices ?? []
    ).find((c: IQuestionnaireChoice) => c.value === selectedValue);
    const match = (choice?.text ?? '').match(TEN_METER_WALK_TEST_LEVEL_REGEX);

    return (
      !!match && Number(match[1]) === TEN_METER_WALK_TEST_TOTAL_ASSISTANCE
    );
  }
}
